import React from 'react';
import { Link } from 'react-router-dom';
import { 
  BuildingOfficeIcon, 
  CubeIcon, 
  BeakerIcon, 
  TruckIcon,
  ServerIcon,
  BuildingLibraryIcon,
  CogIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';

const IndustryCard = ({ title, icon: Icon, description }) => (
  <div className="p-6 rounded-xl bg-figma-dark-bg-secondary/40 backdrop-blur-sm border border-figma-dark-border hover:border-figma-dark-primary transition-all duration-300 group">
    <div className="flex items-start space-x-4">
      <div className="p-2 rounded-lg bg-figma-dark-bg-primary/40 group-hover:bg-figma-dark-primary/10 transition-colors">
        <Icon className="w-6 h-6 text-figma-dark-primary" />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-figma-dark-text-primary mb-2">{title}</h3>
        {description && (
          <p className="text-figma-dark-text-secondary text-sm">{description}</p>
        )}
      </div>
    </div>
  </div>
);

const Industries = () => {
  const industries = [
    "Aerospace",
    "Automotive",
    "Battery Manufacturing",
    "Campus",
    "Chemical Industry",
    "Cement",
    "Commercial Buildings",
    "Cranes",
    "Data Centers",
    "Distributors",
    "Food and beverage industry",
    "Digital glass production",
    "Healthcare",
    "Higher education",
    "Mining industry",
    "Panel Building",
    "Pharmaceuticals",
    "Power Utilities",
    "Tire manufacturing",
    "Transportation and Logistics",
    "Water Industry",
    "Wind Equipment",
    "Marine",
    "Machine Tools"
  ];

  return (
    <div className="min-h-screen bg-figma-dark-bg-primary text-figma-dark-text-primary">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-figma-dark-bg-primary/60 to-figma-dark-bg-secondary/60"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(124,58,237,0.15),transparent_50%)]"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <div className="inline-block mb-6 animate-fade-in-up">
              <span className="px-4 py-2 rounded-full bg-figma-dark-bg-secondary/50 backdrop-blur-sm border border-figma-dark-border text-figma-dark-text-secondary text-sm">
                Industry Solutions
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in-up">
              <span className="bg-gradient-to-r from-figma-dark-primary to-figma-dark-accent bg-clip-text text-transparent">
                Transforming Industries
              </span>
              <br />
              Through Innovation
            </h1>
            <p className="text-xl text-figma-dark-text-secondary mb-8 max-w-3xl mx-auto animate-fade-in-up">
              Discover how our solutions are revolutionizing diverse industries and driving digital transformation.
            </p>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-20 bg-figma-dark-bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-figma-dark-text-primary">Industries We Serve</h2>
            <p className="text-figma-dark-text-secondary">Comprehensive solutions across multiple sectors</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((industry, index) => (
              <IndustryCard
                key={index}
                title={industry}
                icon={CogIcon}
                description="Innovative solutions tailored for industry-specific challenges"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Webinar Section */}
      <section className="py-20 bg-figma-dark-bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-figma-dark-bg-secondary/40 backdrop-blur-sm border border-figma-dark-border rounded-2xl p-8 md:p-12">
            <div className="max-w-3xl">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-figma-dark-text-primary">
                Thinking Automation Ahead Webinar Series
              </h2>
              <p className="text-figma-dark-text-secondary mb-8">
                Join our upcoming webinar series to explore future-ready solutions and discover how next-gen automation through digitalization is transforming industries, making them more secure, scalable, and sustainable.
              </p>
              <Link
                to="/register"
                className="inline-block px-8 py-4 rounded-full bg-gradient-to-r from-figma-dark-primary to-figma-dark-accent text-white font-medium hover:shadow-figma-dark-glow transition-all duration-300 hover:scale-105"
              >
                Register now
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Latest News Section */}
      <section className="py-20 bg-figma-dark-bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-12 text-figma-dark-text-primary">Latest Updates</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-figma-dark-bg-primary/40 backdrop-blur-sm border border-figma-dark-border rounded-xl p-6">
              <h3 className="text-xl font-semibold mb-4 text-figma-dark-text-primary">Siemens consortium partners with BMRCL</h3>
              <p className="text-figma-dark-text-secondary mb-6">
                Project to improve connectivity and increase the share of sustainable public transport in Bengaluru & to equip Bengaluru Metro Phase 2 spanning over 58 kilometers connecting Bengaluru Airport terminal to Central Silk Board via KR Puram.
              </p>
              <Link
                to="/news/bmrcl-partnership"
                className="text-figma-dark-primary hover:text-figma-dark-primary-hover font-medium"
              >
                Learn more →
              </Link>
            </div>
            <div className="bg-figma-dark-bg-primary/40 backdrop-blur-sm border border-figma-dark-border rounded-xl p-6">
              <h3 className="text-xl font-semibold mb-4 text-figma-dark-text-primary">Low Voltage Switchgear Factory, Kalwa</h3>
              <p className="text-figma-dark-text-secondary mb-6">
                Siemens Limited announces CAPEX and continues to invest in state-of-the-art manufacturing facilities.
              </p>
              <Link
                to="/news/switchgear-factory"
                className="text-figma-dark-primary hover:text-figma-dark-primary-hover font-medium"
              >
                Learn more →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Industries; 